import { PropsWithChildren, useMemo } from 'react';

import {
  useHostingAdvancedServer,
  useHostingAdvancedSsoWhm,
} from '@newfold/huapi-js';

import useAccount from '~/hooks/useAccount';
import {
  productTypes,
  useCtbProductInfo,
  useCtbPurchaseStatusQuery,
} from '~/hooks/useCtbProductInfo';

import SSOComponent from '../index';
import { SSO_HOOK_OPTIONS } from '../utils/helpers';
import { BaseSsoProps } from '../utils/types';

interface WhmSSOProps extends BaseSsoProps {
  hostingId?: string | number;
}

const WhmSSO = ({
  children = undefined,
  hostingId = undefined,
  queryParams = {},
  disabled = false,
  ...props
}: PropsWithChildren<WhmSSOProps>) => {
  const { id: accountHostingId } = useAccount();
  const accountId = hostingId || accountHostingId;

  // Get server status for quicklinks validation
  const { data: serverData } = useHostingAdvancedServer(accountId, {
    query: {
      ...SSO_HOOK_OPTIONS.query,
      enabled: true,
    },
  });
  const serverStatus = serverData?.data?.status;

  // CTB upgrade in progress check
  const productInfo = useCtbProductInfo(productTypes.HOSTING_UPGRADE);
  const { hasPurchasedRecently } = useCtbPurchaseStatusQuery(
    productInfo.purchaseQueryKey,
  );

  const sso = useHostingAdvancedSsoWhm(accountId, {
    query: {
      ...SSO_HOOK_OPTIONS.query,
      enabled: queryParams?.app === 'whm',
    },
  });

  // Disable quicklinks for WHM based on server status or CTB upgrade status
  const isQuickLinksDisabled = useMemo(() => {
    if (queryParams?.app === 'whm') {
      const serverStates = ['active'];
      if (!serverStates.includes(serverStatus!) || hasPurchasedRecently) {
        return true;
      }
    }
    return false;
  }, [queryParams?.app, serverStatus, hasPurchasedRecently]);

  return (
    <SSOComponent
      sso={sso}
      isDisabled={disabled || isQuickLinksDisabled}
      hostingId={hostingId}
      {...props}
    >
      {children}
    </SSOComponent>
  );
};

export default WhmSSO;
